import { useState, useEffect, useCallback } from 'react';
import { lmsClient } from '@/lib/api/lmsClient';
import type { MoodleCourse } from '@/types/moodle.types';

export const useCourses = () => {
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [coursesData, categoriesData] = await Promise.all([
        lmsClient.courses.getCourses(),
        lmsClient.courses.getCategories(),
      ]);
      setCourses(coursesData);

      // Only show categories that have at least one course the user is enrolled in
      const enrolledCategoryIds = new Set(coursesData.map((c) => c.category));
      const activeCategories = categoriesData
        .filter((c) => enrolledCategoryIds.has(c.id.toString()))
        .map((c) => ({ id: c.id.toString(), name: c.name }));

      setCategories([{ id: 'all', name: 'All Courses' }, ...activeCategories]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { courses, categories, loading, error, retry: fetchAll };
};
